<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRouter, isNavigationFailure } from 'vue-router'
import { useRateCheck } from './useRateCheck'
import type { RateCheckDisplayStatus } from './useRateCheck'
import RateCheckDisplay from './RateCheckDisplay.vue'
import appAnalytics from '@/utils/tracking'
import { selectedAccommodationId } from '@/layouts/queries'
import { getContactUrl } from '@/utils/Utility'
import { useSessionValues } from '@/composables/useSessionValues'
import { useConnectivityProviderName } from '@/composables/useConnectivityProviderName'
import { useCampaign } from '@/components/rateConnect/queries'
import useToastNotifications from '@/components/notifications/useToastNotifications'

const router = useRouter()
const { language, userId } = useSessionValues()
const { status } = useRateCheck()
const { currentPartner, isLoading: isCampaignLoading } = useCampaign(selectedAccommodationId)
const { displayNotification } = useToastNotifications()

const { providerName } = useConnectivityProviderName(currentPartner)

const contactUsUrl = computed(() =>
  getContactUrl(language.value, userId.value ?? null, selectedAccommodationId.value, true)
)

// `currentPartner` (from useCampaign) may still be loading when this screen
// mounts, so wait for it to settle before firing the started event — this
// ensures connectivity_provider is populated instead of always being undefined.
// `immediate: true` covers the case where the campaign query is already
// cached (staleTime: Infinity) from earlier in the setup flow.
let hasTrackedStart = false
watch(
  isCampaignLoading,
  loading => {
    if (loading || hasTrackedStart) return
    hasTrackedStart = true
    appAnalytics.track(appAnalytics.events.RC_RATE_CHECK_STARTED, {
      accommodationId: selectedAccommodationId.value,
      connectivity_provider: currentPartner.value,
    })
  },
  { immediate: true }
)

// Toast is always green for the rate check flow, regardless of status.
const toastMessageMap: Record<Exclude<RateCheckDisplayStatus, 'loading'>, string> = {
  available: 'rc_rate_check_success',
  unknown: 'rc_rate_check_fail_message',
  unavailable: 'rc_rate_check_no_rates',
}

// Navigates away from the rate check stepper and, once navigation has
// resolved (i.e. the stepper has actually closed), shows the toast. This
// keeps the toast from appearing while the stepper/error screen is still
// on screen.
const navigateToRateConnect = (analyticsEvent: string, toastMessage?: string) => {
  if (analyticsEvent) {
    appAnalytics.track(analyticsEvent, { connectivity_provider: currentPartner.value })
  }
  router.push({ name: 'rate-connect-running-performance' }).then(failure => {
    if (!toastMessage || isNavigationFailure(failure)) return
    displayNotification({
      message: toastMessage,
      type: 'success',
      isTranslationKey: true,
      isHtml: true,
    })
  })
}

watch(status, (newStatus) => {
  if (newStatus !== 'available') return
  navigateToRateConnect(appAnalytics.events.RC_RATE_CHECK_SUCCESS, toastMessageMap.available)
})

const handleContinue = () => {
  const eventMap: Record<RateCheckDisplayStatus, string> = {
    available: '',
    unavailable: appAnalytics.events.RC_RATE_CHECK_FAILURE_CONTINUE,
    unknown: appAnalytics.events.RC_RATE_CHECK_UNKNOWN_CONTINUE,
    loading: '',
  }
  const toastMessage = status.value === 'loading' ? undefined : toastMessageMap[status.value]
  navigateToRateConnect(eventMap[status.value], toastMessage)
}

const handleContactUs = () => {
  appAnalytics.track(appAnalytics.events.RC_RATE_CHECK_CONTACT_US_CLICKED, {
    accommodationId: selectedAccommodationId.value,
    connectivity_provider: currentPartner.value,
  })
  window.open(contactUsUrl.value, '_blank', 'noopener,noreferrer')
}
</script>

<template>
  <RateCheckDisplay
    :status="status"
    :providerName="providerName"
    :partnerId="currentPartner"
    :contactUsUrl="contactUsUrl"
    @continue="handleContinue"
    @contactUs="handleContactUs"
  />
</template>
